/* eslint-disable @typescript-eslint/camelcase */

import React from 'react'
import { IntlProvider } from 'react-intl'
import { ThemeProvider } from 'styled-components'

import { theme } from '@elamajs/ui-kit'

import {
  DoubleCompanyChart,
  CPMChart,
  ViewPerAdvertiserChart,
  ThemeRankChart,
  ExpensesAverageChart,
  ViewPerAdChart,
  CRChart,
  DoubleClickChart
} from './charts'
import * as Styled from './Visualisation.styled'
import { Chart } from './components'
import { useData, useMediaQuery } from './hooks'
import { GraphPayload } from './types'
import Filters from './Filters'

const Visualisation = () => {
  const { data, fetchData, isFetching } = useData()

  const displayType = useMediaQuery()

  return (
    <Styled.Main data-sticky-container>
      <Filters data={data} isFetching={isFetching} fetchData={fetchData} />
      <Styled.Body>
        <Styled.Row>
          <Styled.Title>Сколько рекламодатели тратят на одно объявление</Styled.Title>
          <Chart
            graphHeight={300}
            legend={[
              {
                color: '#C88CF2',
                title: (
                  <>
                    Количество объявлений{displayType === 'mobile' ? <br /> : ' '}на рекламодателя
                  </>
                ),
                tooltip: (
                  <>Среднее количество кампаний на&nbsp;одного рекламодателя в&nbsp;тематике</>
                )
              },
              {
                color: '#3A64F8',
                title: 'Затраты на одно объявление',
                tooltip: (
                  <>
                    Средние затраты на&nbsp;одну рекламную кампанию в&nbsp;тематике рекламодателей
                  </>
                )
              }
            ]}
            data={
              data?.data?.ad_count?.length && data?.data?.ad_expenses?.length
                ? (({
                    ad_count: data.data.ad_count,
                    ad_expenses: data.data.ad_expenses
                  } as unknown) as GraphPayload)
                : undefined
            }
          >
            <DoubleCompanyChart
              graphHeight={300}
              data={
                data?.data?.ad_count && data?.data?.ad_expenses
                  ? { ad_count: data.data.ad_count, ad_expenses: data.data.ad_expenses }
                  : undefined
              }
            />
          </Chart>
        </Styled.Row>
        <Styled.Row>
          <Styled.Title>Сколько тратит и&nbsp;получает показов один рекламодатель</Styled.Title>
          <Styled.FlexContainer>
            <Chart
              title={
                <>
                  Среднее количество{displayType === 'mobile' ? <br /> : ' '}показов
                  на&nbsp;рекламодателя
                </>
              }
              tooltip={
                <>
                  Средние количество просмотров на&nbsp;одного рекламодателя в&nbsp;тематике.
                  В&nbsp;расчете учтены просмотры по&nbsp;всем кампаниям рекламодателя
                  в&nbsp;периоде
                </>
              }
              data={data?.data.view_per_advertiser_average}
              withAverage
              graphHeight={350}
              averageMarginLeft={60}
            >
              <ViewPerAdvertiserChart
                graphHeight={350}
                data={data?.data.view_per_advertiser_average}
              />
            </Chart>
            <Chart
              title={
                <>Средние затраты{displayType === 'mobile' ? <br /> : ' '}на&nbsp;рекламодателя</>
              }
              tooltip={
                <>
                  Средние затраты на&nbsp;одного рекламодателя в&nbsp;тематике. В&nbsp;расчете
                  учтены все запущенные кампании рекламодателя в&nbsp;периоде
                </>
              }
              data={data?.data.expenses_average}
              withAverage
              isAverageCurrency
              graphHeight={350}
              averageMarginLeft={50}
            >
              <ExpensesAverageChart graphHeight={350} data={data?.data.expenses_average} />
            </Chart>
          </Styled.FlexContainer>
        </Styled.Row>
        <Styled.Row>
          <Styled.Title>
            Какая средняя конверсия и&nbsp;количество показов для объявления
          </Styled.Title>
          <Styled.FlexContainer>
            <Chart
              title={
                <>
                  Среднее количество просмотров{displayType === 'mobile' ? <br /> : ' '}
                  на&nbsp;объявление
                </>
              }
              tooltip={
                <>
                  Среднее количество просмотров на&nbsp;одну кампанию в&nbsp;тематике рекламодателей
                </>
              }
              data={data?.data.view_per_ad_average}
              graphHeight={350}
            >
              <ViewPerAdChart graphHeight={350} data={data?.data.view_per_ad_average} />
            </Chart>
            <Chart
              title={
                <>
                  Средняя конверсия{displayType === 'mobile' ? <br /> : ' '}на&nbsp;объявление (CR)
                </>
              }
              tooltip={
                <>
                  Средняя конверсия из&nbsp;просмотров в&nbsp;подписчиков/стартовавших бот
                  пользователей
                </>
              }
              data={data?.data.cr}
              graphHeight={350}
            >
              <CRChart graphHeight={350} data={data?.data.cr} />
            </Chart>
          </Styled.FlexContainer>
        </Styled.Row>
        <Styled.Row>
          <Styled.Title>Сколько стоит подписчик и&nbsp;1000 показов</Styled.Title>
          <Chart
            graphHeight={200}
            title={
              <>
                Стоимость привлечения одного{displayType === 'mobile' ? <br /> : ' '}подписчика
                (CPJ/CPS)
              </>
            }
            tooltip={
              <>
                CPJ&nbsp;&mdash; средневзвешенная стоимость одного подписчика. CPS&nbsp;&mdash;
                средневзвешенная стоимость одного стартовавшего бота
                <br />
                <br />
                CPJ/CPS = сумма затрат на&nbsp;кампанию в&nbsp;тематике рекламодателей поделенное
                на&nbsp;количество подписок/стартов бота в&nbsp;тематике рекламодателей
              </>
            }
            data={data?.data.cpj}
            withAverage
            isAverageCurrency
          >
            <CPMChart data={data?.data.cpj} graphHeight={200} />
          </Chart>
          <Chart
            graphHeight={200}
            title="Стоимость 1000 показов (CPM)"
            tooltip={
              <>
                Средневзвешенная стоимость за&nbsp;1000 показов
                <br />
                <br />
                CPM в&nbsp;тематике = сумма затрат на&nbsp;кампанию в&nbsp;тематике рекламодателей
                поделенное на&nbsp;количество просмотров в&nbsp;тематике рекламодателей или 1000
              </>
            }
            data={data?.data.cpm}
            withAverage
            isAverageCurrency
          >
            <CPMChart data={data?.data.cpm} graphHeight={200} />
          </Chart>
        </Styled.Row>
        <Styled.Row>
          <Styled.Title>Сколько в&nbsp;среднем кликов и&nbsp;какая цена за&nbsp;клик</Styled.Title>
          <Chart
            graphHeight={300}
            legend={[
              {
                color: '#1CC089',
                title: 'Средняя цена за клик (CPC)'
              },
              {
                color: '#FB606D',
                title: 'Среднее количество кликов'
              }
            ]}
            data={
              data?.data?.cpc?.length && data?.data?.click_per_ad_average?.length
                ? (({
                    cpc: data.data.cpc,
                    click_per_ad_average: data.data.click_per_ad_average
                  } as unknown) as GraphPayload)
                : undefined
            }
          >
            <DoubleClickChart
              graphHeight={300}
              data={
                data?.data?.cpc && data?.data?.click_per_ad_average
                  ? { cpc: data.data.cpc, click_per_ad_average: data.data.click_per_ad_average }
                  : undefined
              }
            />
          </Chart>
        </Styled.Row>
        <Styled.Row>
          <ThemeRankChart data={data?.data.category_top} />
        </Styled.Row>
      </Styled.Body>
    </Styled.Main>
  )
}

const withProviders = (Component: React.ComponentType) => {
  const NewComponent: React.ComponentType = () => {
    return (
      <ThemeProvider theme={theme}>
        <IntlProvider locale="ru">
          <Component />
        </IntlProvider>
      </ThemeProvider>
    )
  }

  return NewComponent
}

export default withProviders(Visualisation)
